import { dataService } from '../apis/dataService';
import type {
  BalanceSheet,
  CashflowStatement,
  ClientOverview,
  ClientUnit,
  Currency,
  IncomeStatement,
  Unit,
} from '../../types/api';
import { convertUSDToCurrency } from './currencyConversion';
import {
  convertUnitValueToTargetCurrency,
  describePeriodWindow,
  fetchStatementsForUnits,
  findLatestReportedDate,
  groupStatementRowsByUnit,
  hasCategoryData,
  resolvePeriodWindow,
  sumCategoryValueAtWindowEnd,
  sumCategoryValueInWindow,
  type PeriodMode,
} from './unitFinancials';

const EQUITY_VALUATION_CODE = 13;
const NET_OPERATING_INCOME_CODE = 24;
const NET_CASH_CODE = 30;
const CASH_IN_FROM_OPERATION_CODE = 2;

export type PropertyAttentionId = 'negativeCashFlow' | 'negativeNoi' | 'noEquityValuation';

export interface PropertyAttentionFlag {
  id: PropertyAttentionId;
  label: string;
}

export interface PropertyNativeValue {
  value: number;
  currencyCode: string;
}

export interface PropertyExplorerRow {
  unitId: number;
  name: string;
  locationLabel: string;
  operatingModel: string;
  ownershipPercent: number;
  marketValue: number;
  /** Only present when the unit trades in a currency other than the display one. */
  marketValueNative: PropertyNativeValue | null;
  netEquity: number;
  noi: number | null;
  netCashFlow: number | null;
  cocReturn: number | null;
  attention: PropertyAttentionFlag[];
  searchText: string;
}

export interface PropertyExplorerRowsInput {
  userId: number;
  clientUnits: ClientUnit[];
  overviewData: ClientOverview[];
  allUnits: Unit[];
  targetCurrency: string;
  currencies: Currency[];
}

export interface PropertyExplorerStatements {
  balanceSheetRows: Array<BalanceSheet & { unit_id_no: number }>;
  incomeRows: Array<IncomeStatement & { unit_id_no: number }>;
  cashflowRows: Array<CashflowStatement & { unit_id_no: number }>;
}

export interface PropertyExplorerData {
  rows: PropertyExplorerRow[];
  /** Mid-sentence window description, for example `the 12 months to Oct 2024`. */
  periodCaption: string;
}

/** Properties have no dedicated name field, so fall back the same way the map tooltips do. */
function buildPropertyName(unit: Unit | undefined, unitId: number): string {
  return unit?.address || unit?.city || `Unit ${unitId}`;
}

function buildLocationLabel(unit: Unit | undefined, clientUnit: ClientUnit | undefined): string {
  const city = unit?.city?.trim();
  const country = unit?.country?.trim() || clientUnit?.country?.trim();

  if (city && country) return `${city}, ${country}`;
  return city || country || '—';
}

/**
 * Ownership arrives as a fraction (0.5) from the warehouse, but tolerate
 * values already expressed as percentages so the column never shows 5000%.
 */
function toOwnershipPercent(percentOwnership: number | undefined): number {
  const value = percentOwnership || 0;
  return value > 1 ? value : value * 100;
}

function buildAttentionFlags(
  netCashFlow: number | null,
  noi: number | null,
  hasEquityValuation: boolean
): PropertyAttentionFlag[] {
  const flags: PropertyAttentionFlag[] = [];

  if (netCashFlow !== null && netCashFlow < 0) {
    flags.push({ id: 'negativeCashFlow', label: 'Negative Cash Flow' });
  } else if (noi !== null && noi < 0) {
    flags.push({ id: 'negativeNoi', label: 'Negative NOI' });
  }

  if (!hasEquityValuation) {
    flags.push({ id: 'noEquityValuation', label: 'No Equity Valuation' });
  }

  return flags;
}

export async function loadPropertyExplorerStatements(
  userId: number,
  clientUnits: Array<{ unit_id_no?: number }>
): Promise<PropertyExplorerStatements> {
  if (!clientUnits.length) {
    return { balanceSheetRows: [], incomeRows: [], cashflowRows: [] };
  }

  const [balanceSheetRows, incomeRows, cashflowRows] = await Promise.all([
    fetchStatementsForUnits(userId, clientUnits, dataService.getBalanceSheet),
    fetchStatementsForUnits(userId, clientUnits, dataService.getIncomeStatement),
    fetchStatementsForUnits(userId, clientUnits, dataService.getCashflow),
  ]);

  return { balanceSheetRows, incomeRows, cashflowRows };
}

/**
 * Assembles one table row per property by combining the client overview
 * snapshot with the unit's balance sheet, income statement and cashflow.
 * Flow figures share one period window so properties stay comparable.
 */
export function buildPropertyExplorerRows({
  clientUnits,
  overviewData,
  allUnits,
  targetCurrency,
  currencies,
  statements,
  periodMode,
}: PropertyExplorerRowsInput & {
  statements: PropertyExplorerStatements;
  periodMode: PeriodMode;
}): PropertyExplorerData {
  if (!clientUnits.length || !overviewData.length) {
    return { rows: [], periodCaption: '' };
  }

  const { balanceSheetRows, incomeRows, cashflowRows } = statements;
  const balanceSheetByUnit = groupStatementRowsByUnit(balanceSheetRows);
  const incomeByUnit = groupStatementRowsByUnit(incomeRows);
  const cashflowByUnit = groupStatementRowsByUnit(cashflowRows);

  const latestReportedDate =
    findLatestReportedDate(incomeRows, [NET_OPERATING_INCOME_CODE]) ||
    findLatestReportedDate(cashflowRows, [NET_CASH_CODE, CASH_IN_FROM_OPERATION_CODE]);

  const periodWindow = resolvePeriodWindow(periodMode, latestReportedDate);
  const periodCaption = describePeriodWindow(periodWindow);

  const rows = overviewData.map((overview) => {
    const unitId = overview.unit_id_no;
    const unit = allUnits.find((entry) => entry.unit_id_no === unitId);
    const clientUnit = clientUnits.find((entry) => entry.unit_id_no === unitId);

    const unitBalanceSheet = balanceSheetByUnit.get(unitId) || [];
    const unitIncome = incomeByUnit.get(unitId) || [];
    const unitCashflow = cashflowByUnit.get(unitId) || [];

    const toDisplayCurrency = (value: number) =>
      convertUnitValueToTargetCurrency(value, unitId, allUnits, targetCurrency, currencies);

    const marketValueUsd = overview.assets_market_value_usd || overview.total_assets_usd || 0;
    const nativeCurrencyCode = unit?.currency || overview.unit_currency || 'USD';

    const netEquity = toDisplayCurrency(
      sumCategoryValueAtWindowEnd(unitBalanceSheet, EQUITY_VALUATION_CODE, periodWindow)
    );

    const hasNoiData = hasCategoryData(unitIncome, NET_OPERATING_INCOME_CODE);
    const noi = hasNoiData
      ? toDisplayCurrency(
          sumCategoryValueInWindow(unitIncome, NET_OPERATING_INCOME_CODE, periodWindow)
        )
      : null;

    const hasCashFlowData = hasCategoryData(unitCashflow, NET_CASH_CODE);
    const netCashFlow = hasCashFlowData
      ? toDisplayCurrency(sumCategoryValueInWindow(unitCashflow, NET_CASH_CODE, periodWindow))
      : null;

    const cashReceived = toDisplayCurrency(
      sumCategoryValueInWindow(unitCashflow, CASH_IN_FROM_OPERATION_CODE, periodWindow)
    );

    const name = buildPropertyName(unit, unitId);
    const locationLabel = buildLocationLabel(unit, clientUnit);
    const operatingModel = unit?.operating_model || clientUnit?.operating_model || '—';

    return {
      unitId,
      name,
      locationLabel,
      operatingModel,
      ownershipPercent: toOwnershipPercent(overview.percent_ownership),
      marketValue: convertUSDToCurrency(marketValueUsd, targetCurrency, currencies),
      marketValueNative:
        nativeCurrencyCode && nativeCurrencyCode !== targetCurrency
          ? {
              value: convertUSDToCurrency(marketValueUsd, nativeCurrencyCode, currencies),
              currencyCode: nativeCurrencyCode,
            }
          : null,
      netEquity,
      noi,
      netCashFlow,
      cocReturn: netEquity > 0 ? (cashReceived / netEquity) * 100 : null,
      attention: buildAttentionFlags(
        netCashFlow,
        noi,
        hasCategoryData(unitBalanceSheet, EQUITY_VALUATION_CODE)
      ),
      searchText: `${name} ${locationLabel} ${operatingModel}`.toLowerCase(),
    } satisfies PropertyExplorerRow;
  });

  return {
    rows: rows.sort((first, second) => second.marketValue - first.marketValue),
    periodCaption,
  };
}

export async function fetchPropertyExplorerRows(
  input: PropertyExplorerRowsInput & { periodMode?: PeriodMode }
): Promise<PropertyExplorerData> {
  const statements = await loadPropertyExplorerStatements(input.userId, input.clientUnits);
  return buildPropertyExplorerRows({
    ...input,
    statements,
    periodMode: input.periodMode ?? 'ttm',
  });
}

export function filterPropertyExplorerRows(
  rows: PropertyExplorerRow[],
  searchTerm: string
): PropertyExplorerRow[] {
  const normalized = searchTerm.trim().toLowerCase();

  if (!normalized) {
    return rows;
  }

  return rows.filter((row) => row.searchText.includes(normalized));
}
